import React, { useEffect, useState, useRef, useCallback } from "react";

import { filter, findIndex, get, isEmpty, map, size } from "lodash";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import propTypes from "prop-types";

import PlayListSearchSection from "./SearchSection";
import { ICONS } from "../../constants/icons.constants";
import { ROUTES } from "../../constants/routes.constants";
import { getSongs } from "./editPlayListPage.helpers";
import playlistStore from "../../zustand/playlist.store";
import { showToaster } from "../../helpers/toaster.helpers";
import { getPlaylistUpdatePayload } from "../../helpers/playlist.helpers";
import { addPlaylist, updatePlaylist } from "../../actions/playlist.actions";
import musicPlayerStore from "../../zustand/musicPlayer.store";
import ThumbNail from "../../components/thumbNail/ThumbNail";
import { playlistActions } from "../../actions";
import { indexDbHelpers, toasterHelpers } from "../../helpers";
import Icons from "../../components/icons";
import PlayListSongItem from "./SongItem";
import { shuffleArray } from "../../helpers/music.helpers";

import Loader from "../myPlaylistPage/Loader";
import userStore from "../../zustand/user.store";
import { Button, ButtonLoader } from "../../kaya";
import CheckIcon from "../../components/icons/CheckIcon";
import imageStore from "../../zustand/image.store";
import {
  downloadPlaylistImage,
  downloadSongImage,
} from "../../helpers/generel.helpers";

import styles from "./editPlaylistPage.module.scss";
import ignoredSongStore from "../../zustand/ignoreSong.store";
import { socketServices } from "../../services/socket.service";

const EditPlaylistPage = (props) => {
  const { userOffline } = userStore((state) => ({
    userOffline: state.userOffline,
  }));
  const navigate = useNavigate();
  const currentPath = useLocation();
  const params = useParams();

  const {
    playlists,
    addNewPlaylist,
    updatePlaylist: updatePlaylistState,
  } = playlistStore((state) => ({
    playlists: state.playlists,
    addNewPlaylist: state.addNewPlaylist,
    updatePlaylist: state.updatePlaylist,
  }));

  const { setPlayList, isShuffle, setBeforeShuffleSongs } = musicPlayerStore(
    (state) => ({
      setPlayList: state.setPlayList,
      isShuffle: state.isShuffle,
      setBeforeShuffleSongs: state.setBeforeShuffleSongs,
    })
  );

  const { addImage } = imageStore((state) => ({
    addImage: state.addImage,
  }));

  const { songs: ignoredSongs } = ignoredSongStore((state) => ({
    songs: state.songs,
  }));

  const [isNameEdit, setIsNameEdit] = useState(false);
  const [values, setValues] = useState({
    visibleImage: "", // this we'll show in the UI
    imageForBE: null, // this we'll send to the server
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(true);
  const [isPlaylistFetching, setIsPlaylistFetching] = useState(false);

  const isSomethingChanged = useRef(false);

  const currentPlayList = getSongs(params, playlists);
  const {
    songs: initialSongs,
    name,
    isNewPlayList,
    thumbnail_img: image,
  } = currentPlayList;

  useEffect(() => {
    setValues({
      songs: initialSongs,
      name: name,
    });
  }, [params?.id, playlists]);

  useEffect(() => {
    if (!isNewPlayList) {
      setValues((prev) => ({
        ...prev,
        songs: initialSongs,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditClick = () => {
    if (userOffline)
      return showToaster(
        "error",
        "You are offline, please check your internet connection"
      );
    setIsViewMode(false);
    setIsNameEdit(!isNameEdit);
  };

  const handleInputChange = (id) => (e) => {
    isSomethingChanged.current = true;
    setValues({
      ...values,
      [id]: e.target.value,
    });
  };

  const handleBack = () => {
    navigate(ROUTES.MY_PLAYLIST);
  };

  const addSong = (newSong) => {
    isSomethingChanged.current = true;

    setValues({
      ...values,
      songs: [...(values?.songs || []), newSong],
    });
  };

  const removeSong = (id) => {
    const updatedSongs = filter(values?.songs, (song) => song.id !== id);
    setValues({
      ...values,
      songs: updatedSongs,
    });
  };

  const handleAddNewPlaylist = async () => {
    setIsLoading(true);
    const payload = getPlaylistUpdatePayload(values);

    const playlist = await addPlaylist(payload);

    setIsLoading(false);

    if (isEmpty(playlist)) {
      toasterHelpers.showToaster("error", "Something went wrong");
      return;
    }

    addNewPlaylist(playlist);
    navigate(ROUTES.MY_PLAYLIST);
  };

  const handelEditPlaylist = async () => {
    setIsLoading(true);
    const payload = getPlaylistUpdatePayload(values);
    const isSuccess = await updatePlaylist(params.id, payload);
    setIsLoading(false);

    if (!isSuccess) return;

    updatePlaylistState(
      { ...values, visibelePhoto: values.visibleImage },
      params.id
    );
    navigate(ROUTES.MY_PLAYLIST);
  };

  const handleEditSuccess = () => {
    if (isEmpty(values.songs)) {
      showToaster("error", "Please add songs to playlist");

      return;
    }
    if (currentPath.pathname === ROUTES.NEW_PLAYLIST) {
      handleAddNewPlaylist();
    }
    if (!isNewPlayList) {
      handelEditPlaylist();
    }
  };

  const handlePlayClick = (songId) => {
    if (songId && ignoredSongs[songId]) {
      return showToaster("info", "Ignored song can't be played");
    }

    let songIndex = -1;

    if (!songId) {
      songIndex = findIndex(values.songs, (song) => !ignoredSongs[song.id]);
    } else {
      songIndex = findIndex(values.songs, (song) => song.id === songId);
    }

    if (songIndex === -1) {
      songIndex = 0;
    }

    const shuffledSongs = isShuffle
      ? shuffleArray([...values.songs], songIndex)
      : values.songs;

    if (isShuffle) {
      setBeforeShuffleSongs([...values.songs]);
    }

    const playlistToStore = {
      ...values,
      songs: shuffledSongs,
    };
    setPlayList(playlistToStore, songIndex);
    navigate(ROUTES.HOME);

    const payload = {
      song_id: currentPlayList?.songs?.[0],
      playlist_id: currentPlayList?.id,
    };
    // sendEvent({ type: 'TOGGLE_PLAY_STATE', status: false });

    socketServices.sendEvent({ type: "SONG_CHANGED", ...payload });
  };

  useEffect(() => {
    if (isEmpty(params.id)) return;
    (async () => {
      if (userOffline) return;
      setIsPlaylistFetching(true);
      const playList = await playlistActions.fetchPlaylistById(params.id);

      if (isEmpty(playList)) {
        setIsPlaylistFetching(false);
        return;
      }
      setIsPlaylistFetching(false);

      setValues(playList);
    })();
  }, [userOffline, params.id]);

  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const handleDownloadClick = async () => {
    setDownloading(true);

    const idExist = await indexDbHelpers.isExistPlaylistInIndexDb(values.id);

    if (idExist) {
      //show toaster
      showToaster("error", "Playlist already downloaded");

      // setDownloading(false);
      return;
    } else {
      try {
        const data = await indexDbHelpers.storePlaylistInIndexDB(values);
        if (data) {
          // push to downloaded
          setDownloaded(true);

          setDownloading(false);
        }
      } catch (error) {
        console.log(error, "error in catch");

        setDownloading(false);
      }
    }

    setDownloading(false);
  };

  useEffect(() => {
    setDownloaded(false);
    if (isEmpty(values.id)) return;

    (async () => {
      const idExist = await indexDbHelpers.isExistPlaylistInIndexDb(values.id);
      if (idExist) {
        setDownloaded(true);
      } else {
        setDownloaded(false);
      }
    })();
  }, [values]);

  const donwloadPlayListImage = useCallback(async () => {
    if (!image) return;
    const isSucess = await downloadPlaylistImage(image);
    if (isSucess) {
      addImage(image);
    }
  }, [addImage, image]);

  const donwloadSongImages = useCallback(async () => {
    const songs = values?.songs || [];
    for (let index = 0; index < size(songs); index++) {
      if (index >= size(songs)) return;

      const imageName = get(songs?.[index], "imageFileName");
      try {
        const result = await downloadSongImage(imageName);
        if (result) {
          addImage(imageName);
        }
      } catch (error) {
        //
      }
    }
  }, [values.songs, addImage]);

  useEffect(() => {
    donwloadSongImages();
    donwloadPlayListImage();
  }, [donwloadSongImages, donwloadPlayListImage]);

  return (
    <div className={styles.pageContent}>
      <div className={styles.left}>
        <div className={styles.header}>
          <div>
            <div onClick={handleBack} className={styles.backIcon}>
              <Icons type={ICONS.BACK_ARROW} />
            </div>
            {!isNewPlayList ? <p>{name}</p> : <p>Create Playlist</p>}
          </div>
        </div>

        <div className={styles.head}>
          <div className={styles.head2}>
            <div
              className={styles.headImg}
              // onClick={() => inputFile.current.click()}
            >
              {/* {!isEmpty(image) ? (

               
                <img
                  src={`https://server.4-play.io/playlists_images/${image}`}
                  alt="img"
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "contain",
                  }}
                  crossOrigin="anonymous"
                />
              ) : (
                <Icons type={ICONS.UPLOAD} />
              )} */}

              <ThumbNail
                name={values.name}
                hasImage={!isEmpty(image)}
                imgSrc={image}
              />
              {/* <input
                // allow only images
                type="file"
                accept="image/*"
                ref={inputFile}
                className={styles.upload}
                onChange={handleUploadImg}
              /> */}
            </div>
            <div className={styles.mainBox}>
              <div className={styles.box1}>
                <div className={styles.box3}>
                  {!isNameEdit ? (
                    <p className={styles.name}>{values.name}</p>
                  ) : (
                    <input
                      className={styles.nameInput}
                      value={values.name}
                      autoFocus={true}
                      onChange={handleInputChange("name")}
                      onBlur={handleEditClick}
                    />
                  )}
                  <div className={styles.backIcon} onClick={handleEditClick}>
                    <Icons type={ICONS.EDIT} />
                  </div>
                </div>
                <div className={styles.editBtn}>
                  {isViewMode ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Button
                        className={styles.playBtn}
                        disabled={isEmpty(values.songs)}
                        isLoading={isLoading}
                        onClick={() => handlePlayClick()}
                        type="Primary"
                      >
                        Play
                      </Button>
                    </div>
                  ) : (
                    <Button
                      isLoading={isLoading}
                      onClick={handleEditSuccess}
                      disabled={userOffline}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>

              <div className={styles.box2}>
                <p>{`${size(values?.songs)} songs`}</p>
                {downloaded ? (
                  <div className={styles.CheckIcon}>
                    <CheckIcon />
                  </div>
                ) : values.id ? (
                  downloading ? (
                    <div>
                      <ButtonLoader
                        active={true}
                        style={{
                          borderColor: "#949494",
                          borderWidth: "4px",
                        }}
                      />
                    </div>
                  ) : (
                    <div onClick={handleDownloadClick}>
                      <Icons type={ICONS.DOWNLOAD} />
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {isPlaylistFetching ? (
          <Loader />
        ) : (
          <div className={styles.songsBox}>
            {map(values?.songs, (item, i) => {
              return (
                <PlayListSongItem
                  key={item.id}
                  song={item}
                  isEditMode={!isViewMode}
                  handleRemove={removeSong}
                  handleItemClick={() => handlePlayClick(item.id)}
                />
              );
            })}
          </div>
        )}
      </div>
      {currentPath.pathname === ROUTES.NEW_PLAYLIST ? (
        <PlayListSearchSection
          addSong={addSong}
          alreadyAddedSongIds={map(values?.songs, "id")}
          isViewMode={isViewMode}
          setIsViewMode={setIsViewMode}
        />
      ) : null}
    </div>
  );
};

EditPlaylistPage.propTypes = {
  isEdit: propTypes.bool,
};

EditPlaylistPage.defaultProps = {
  isEdit: false,
};

export default EditPlaylistPage;
